/**
 * <Popover> — Hybrid Popover primitive (D-05).
 *
 * Wraps `@radix-ui/react-popover`. Unlike Dialog, popovers do NOT require a
 * title at compile time — the anchoring trigger Button typically carries its
 * own accessible name (which, for icon-only triggers, is already enforced by
 * Button's `size="icon"` NonEmptyString aria-label contract from 04a).
 *
 * Composed API: `<Popover trigger={…} content={…}>`.
 * Decomposed: `Popover.Root / Trigger / Content / Close / Anchor`.
 */
import * as React from "react";
import * as RadixPopover from "@radix-ui/react-popover";
import { cn } from "../../utils";

// ---------------------------------------------------------------------------
// Decomposed — styled Radix parts
// ---------------------------------------------------------------------------

export const PopoverRoot = RadixPopover.Root;
export const PopoverTrigger = RadixPopover.Trigger;
export const PopoverAnchor = RadixPopover.Anchor;
export const PopoverClose = RadixPopover.Close;
export const PopoverPortal = RadixPopover.Portal;

export const PopoverContent = React.forwardRef<
  React.ElementRef<typeof RadixPopover.Content>,
  React.ComponentPropsWithoutRef<typeof RadixPopover.Content>
>(function PopoverContent(
  { className, sideOffset = 8, align = "center", children, onWheel, onTouchMove, ...rest },
  ref,
) {
  return (
    <RadixPopover.Portal>
      <RadixPopover.Content
        ref={ref}
        sideOffset={sideOffset}
        align={align}
        // Portaled outside any open Dialog, whose scroll lock cancels wheel/touch at document: stop it first.
        onWheel={(e) => {
          onWheel?.(e);
          e.stopPropagation();
        }}
        onTouchMove={(e) => {
          onTouchMove?.(e);
          e.stopPropagation();
        }}
        className={cn(
          "linkhr-glass z-50 w-72 rounded-md border border-subtle bg-surface-elevated shadow-lg",
          "p-md text-body",
          // Motion — pop: fade + scale 0.98, 140ms; transform-origin follows the
          // Radix data-side (set globally in motion-utilities.css). PRM → fade only.
          "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-98 data-[state=open]:motion-popover",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-98 data-[state=closed]:motion-popover",
          "motion-reduce:data-[state=open]:animate-none motion-reduce:data-[state=closed]:animate-none",
          className,
        )}
        {...rest}
      >
        {children}
      </RadixPopover.Content>
    </RadixPopover.Portal>
  );
});

// ---------------------------------------------------------------------------
// Composed — single-wrapper convenience (trigger + body)
// ---------------------------------------------------------------------------

export interface PopoverProps {
  /** Optional visible title rendered inside the popover (non-required unlike Dialog). */
  title?: React.ReactNode;
  /** Trigger element — wrapped in `<Popover.Trigger asChild>`. */
  trigger: React.ReactNode;
  /** Popover body content. */
  children?: React.ReactNode;
  /** Controlled open state. */
  open?: boolean;
  /** Controlled-open change handler. */
  onOpenChange?: (open: boolean) => void;
  /** Placement side. */
  side?: "top" | "right" | "bottom" | "left";
  /** Alignment along the side. */
  align?: "start" | "center" | "end";
  /** Consumer-supplied className forwarded to content. */
  className?: string;
}

function ComposedPopover({
  title,
  trigger,
  children,
  open,
  onOpenChange,
  side,
  align,
  className,
}: PopoverProps) {
  // Radix renders the content with role="dialog". A visible heading that isn't
  // wired to it leaves that dialog anonymous — screen readers announce "dialog"
  // and nothing else. Point the content at the title it already renders.
  const titleId = React.useId();
  const rootProps = {
    ...(open !== undefined ? { open } : {}),
    ...(onOpenChange !== undefined ? { onOpenChange } : {}),
  };
  const contentProps = {
    ...(side !== undefined ? { side } : {}),
    ...(align !== undefined ? { align } : {}),
    ...(className !== undefined ? { className } : {}),
    ...(title !== undefined ? { "aria-labelledby": titleId } : {}),
  };
  return (
    <RadixPopover.Root {...rootProps}>
      <RadixPopover.Trigger asChild>{trigger}</RadixPopover.Trigger>
      <PopoverContent {...contentProps}>
        {title !== undefined && (
          <div id={titleId} className="text-heading mb-sm">
            {title}
          </div>
        )}
        {children}
      </PopoverContent>
    </RadixPopover.Root>
  );
}

// ---------------------------------------------------------------------------
// Dotted composition — matches Card/Select pattern.
// ---------------------------------------------------------------------------

type PopoverComposition = typeof ComposedPopover & {
  Root: typeof PopoverRoot;
  Trigger: typeof PopoverTrigger;
  Content: typeof PopoverContent;
  Close: typeof PopoverClose;
  Anchor: typeof PopoverAnchor;
  Portal: typeof PopoverPortal;
};

export const Popover = ComposedPopover as PopoverComposition;
Popover.Root = PopoverRoot;
Popover.Trigger = PopoverTrigger;
Popover.Content = PopoverContent;
Popover.Close = PopoverClose;
Popover.Anchor = PopoverAnchor;
Popover.Portal = PopoverPortal;
